import { EyeOutlined } from "@ant-design/icons";
import { Button, Space, Tooltip } from "antd";
import { InstallationModeContext } from "contexts/InstallationMode/installation-mode-context";
import { MeterType, useCheckMeterMappingLazyQuery } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useContext, useState } from "react";
import { PositionFunction } from "types/InstallationMode.d";
import InstallationModeButton from "./InstallationModeButton";
import MeterInstallationModeModal from "./MeterInstallationModeModal";

interface MeterInstallationModeViewerProps {
  keyPositionId: string;
  // meterName: string; // Unused prop - commented out
  meterIdentifier: string; // Node MAC ID for CT meters
  meterType: MeterType;
  roleAndPermission: any;
}

const MeterInstallationModeViewer: React.FC<MeterInstallationModeViewerProps> = ({
  keyPositionId,
  // meterName, // Unused parameter - commented out
  meterIdentifier,
  meterType,
  roleAndPermission,
}) => {
  const { currentKeyEntryOfInstaller } = useContext(InstallationModeContext);
  const [showInstallationModal, setShowInstallationModal] = useState(false);
  const [nodeStatusInfo, setNodeStatusInfo] = useState<{
    nodeStatus?: string;
    nodeStatusInInstallationMode?: string;
    lastTransmission?: string;
    nodeMacId?: string;
  } | null>(null);

  // Check if this meter is currently in installation mode
  const isMeterInInstallationMode =
    currentKeyEntryOfInstaller?.keyPositionId === keyPositionId &&
    currentKeyEntryOfInstaller?.positionFunction === PositionFunction.METERING_POINT;

  // Query to get node status information
  const [checkMeterMapping, { loading: loadingNodeStatus }] = useCheckMeterMappingLazyQuery({
    onCompleted: (data) => {
      const mappingStatus = data?.checkMeterMapping;
      if (
        mappingStatus &&
        (mappingStatus.nodeStatus || mappingStatus.nodeStatusInInstallationMode || mappingStatus.lastTransmission)
      ) {
        setNodeStatusInfo({
          nodeStatus: mappingStatus.nodeStatus || undefined,
          nodeStatusInInstallationMode: mappingStatus.nodeStatusInInstallationMode || undefined,
          lastTransmission: mappingStatus.lastTransmission || undefined,
          nodeMacId: meterIdentifier,
        });
      }
    },
    onError: (/* error */) => {
      // Log error to monitoring instead of console
      // console.warn("Failed to load node status:", error);
    },
  });

  const handleOpenInstallationModal = () => {
    // Fetch fresh node status when opening the modal
    checkMeterMapping({
      variables: {
        identifier: meterIdentifier,
        meterType,
      },
    });
    setShowInstallationModal(true);
  };

  const handleCloseInstallationModal = () => {
    setShowInstallationModal(false);
  };

  // Only show for CT meters and when user has permission
  if (!roleAndPermission.isPC() || meterType !== MeterType.Ct) {
    return null;
  }

  return (
    <>
      <Space size="small">
        {/* Installation Mode Control Button */}
        <InstallationModeButton
          keyPositionId={keyPositionId}
          currentPositionFunction={PositionFunction.METERING_POINT}
          isMeterPosition
          activeModal
          showStopWatch={false}
          automaticTestRun={false}
          iconMode
          buttonName={{ enter: "Enter Installation", exit: "Exit Installation" }}
        />

        {/* Installation Mode Modal View Button - Only show when in installation mode */}
        {isMeterInInstallationMode && (
          <Tooltip title="View Installation Mode">
            <Button
              type="text"
              size="middle"
              icon={<EyeOutlined style={{ fontSize: "18px" }} />}
              onClick={handleOpenInstallationModal}
              loading={loadingNodeStatus}
              style={{ padding: "6px 10px", minWidth: "auto" }}
            />
          </Tooltip>
        )}
      </Space>

      {/* Installation Mode Modal */}
      {showInstallationModal && isMeterInInstallationMode && (
        <MeterInstallationModeModal
          visible={showInstallationModal}
          onClose={handleCloseInstallationModal}
          meterPositionId={keyPositionId}
          nodeStatusInfo={nodeStatusInfo || undefined}
        />
      )}
    </>
  );
};

export default MeterInstallationModeViewer;
